import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { FullSpinnerService } from '../../../services/full-spinner.service';
import { HasPermissionDirective } from '../../../utils/has-permission.directive';
import { ConfirmAlertComponent } from '../../shared/confirm-alert/confirm-alert.component';
import { AppConstants } from '../../../utils/AppConstants';
import { ReportTemplate, ReportTemplateVar } from '../../../models/ReportTemplate';
import { ReportTemplateService } from '../../../services/report-template.service';
import { AppEnums } from '../../../models/AppEnums';
import { AlertService } from '../../shared/alert/alert.service';
import { ReportEditor } from '../../reports/shared/components/report-editor/report-editor';
import { applyExampleValues } from '../../../utils/report-preview';

const HEADER_TYPE = 'HEADER';

@Component({
  selector: 'app-report-template-v2-detail',
  standalone: true,
  imports: [
    CommonModule,
    HasPermissionDirective,
    ConfirmAlertComponent,
    FormsModule,
    ReportEditor],
  templateUrl: './report-template-v2-detail.component.html',
  styleUrl: './report-template-v2-detail.component.scss',
})
export class ReportTemplateV2DetailComponent implements OnInit, OnDestroy {
  paramSubscription!: Subscription;
  showConfirmationModal: boolean = false;
  confirmationMessage: string = '';

  id?: number;
  reportTemplate: ReportTemplate = { templateVersion: 2, innerHtml: '', headerHtml: '', enabled: true };
  detail_title: string = 'Nuovo template';

  // Se false il template usa l'intestazione condivisa (tipo HEADER)
  customHeader: boolean = false;
  sharedHeaderHtml: string = '';

  previewHTML!: SafeHtml;

  reportTemplateVariables: ReportTemplateVar[] = [];
  reportTemplateTypes = AppEnums.reportTemplateTypes;

  constructor(
    private sanitizer: DomSanitizer,
    private reportTemplateService: ReportTemplateService,
    private alertService: AlertService,
    private fullSpinnerService: FullSpinnerService,
    private router: Router,
    private activatedRoute: ActivatedRoute) {
    fullSpinnerService.spinner(undefined);
  }

  get isHeaderType(): boolean {
    return this.reportTemplate.type === HEADER_TYPE;
  }

  ngOnInit(): void {
    this.reportTemplateService.findAllVars().subscribe({
      next: (res) => this.reportTemplateVariables = res
    });

    this.reportTemplateService.find({ type: HEADER_TYPE, pageSize: 0, pageNumber: 1 }).subscribe({
      next: (res) => {
        const sharedHeader = res.items.find(t => t.templateVersion === 2);
        this.sharedHeaderHtml = sharedHeader?.innerHtml ?? '';
      }
    });

    this.paramSubscription = this.activatedRoute.params.subscribe(parameter => {
      this.id = parameter['id'];
      if (this.id! > 0) {
        this.fullSpinnerService.spinner('');
        this.reportTemplateService.get(this.id!).subscribe({
          next: (res) => {
            this.reportTemplate = res;
            this.reportTemplate.innerHtml = res.innerHtml ?? '';
            this.reportTemplate.headerHtml = res.headerHtml ?? '';
            this.customHeader = this.reportTemplate.headerHtml !== '';
            this.detail_title = res.name!;
          }, complete: () => {
            this.fullSpinnerService.spinner(undefined);
          }
          , error: () => {
            this.fullSpinnerService.spinner(undefined);
          }
        });
      }
    });
  }

  updatePreview(): void {
    const header = this.isHeaderType ? '' : (this.customHeader ? this.reportTemplate.headerHtml : this.sharedHeaderHtml);
    const html = (header ?? '') + (this.reportTemplate.innerHtml ?? '');
    this.previewHTML = this.sanitizer.bypassSecurityTrustHtml(applyExampleValues(html, this.reportTemplateVariables));
  }

  onSubmit(form: NgForm): void {
    if (!form.valid) {
      form.control.markAllAsTouched();
      return;
    }
    this.fullSpinnerService.spinner('');
    this.reportTemplate.templateVersion = 2;
    if (this.isHeaderType || !this.customHeader) {
      this.reportTemplate.headerHtml = '';
    }
    this.reportTemplateService.save(this.reportTemplate).subscribe({
      next: () => {
        this.alertService.addErrors(AppConstants.titleSuccessMessage, [AppConstants.successMessage], true);
        this.router.navigate(['report-template']);
      }, complete: () => {
        this.fullSpinnerService.spinner(undefined);
      }
      , error: () => {
        this.alertService.addErrors(AppConstants.titleErrorMessage, [AppConstants.errorMessage], true);
        this.fullSpinnerService.spinner(undefined);
      }
    });
  }

  back() {
    this.router.navigate(['report-template']);
  }

  confirmDelete() {
    this.showConfirmationModal = true;
    this.confirmationMessage = "Sicuro di voler eliminare il template " + this.reportTemplate.name + ' ?';
  }

  cancelDelete() {
    this.showConfirmationModal = false;
  }

  delete() {
    this.fullSpinnerService.spinner('');
    this.reportTemplateService.delete(this.reportTemplate.id!).subscribe({
      next: () => {
        this.alertService.addErrors(AppConstants.titleDeleteSuccessMessage, [AppConstants.successMessage], true);
        this.router.navigate(['report-template']);
      }, complete: () => {
        this.fullSpinnerService.spinner(undefined);
      }
      , error: () => {
        this.alertService.addErrors(AppConstants.titleErrorMessage, [AppConstants.errorMessage], true);
        this.fullSpinnerService.spinner(undefined);
      }
    });
  }

  ngOnDestroy(): void {
    this.paramSubscription.unsubscribe();
  }
}
